import { useMutation, useQuery } from '@apollo/client';
import { RequestQuoteOutlined } from '@mui/icons-material';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  useCustomSnackbar,
  CurrencyProvider,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { useNegotiationDetail } from '../Module.hooks';
import { TsrctNegotiationCaseStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { WidgetContentEmpty } from '~/components/WidgetContentEmpty/WidgetContentEmpty';

import { AcceptButton, AcceptButtonProps } from './components/AcceptButton/AcceptButton';
import { DeclineButton, DeclineButtonProps } from './components/DeclineButton/DeclineButton';
import { NegotiatedQuoteDetails } from './components/NegotiatedQuoteDetails/NegotiatedQuoteDetails';
import { NEGOTIATED_QUOTE_ACCEPT_MUTATION } from './NegotiatedQuoteAcceptMutation.gql';
import { NEGOTIATED_QUOTE_DECLINE_MUTATION } from './NegotiatedQuoteDeclineMutation.gql';
import { NEGOTIATED_QUOTE_QUERY } from './NegotiatedQuoteWidget.query.gql';

export interface NegotiatedQuoteWidgetProps {
  // widget is part of a bigger form -> do not add own form and saving capabilities
  hasParentForm?: boolean;
}

export const NegotiatedQuoteWidget = (_props: NegotiatedQuoteWidgetProps) => {
  const { negotiationId } = useNegotiationDetail();
  const { onError } = useCustomSnackbar();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiatedQuoteWidget' });

  const variables = {
    id: negotiationId,
  };
  const refetchQueries = [{ query: NEGOTIATED_QUOTE_QUERY, variables }];

  const { data, loading, error } = useQuery(NEGOTIATED_QUOTE_QUERY, {
    variables,
    skip: !negotiationId,
  });

  const [accept, { loading: acceptLoading }] = useMutation(NEGOTIATED_QUOTE_ACCEPT_MUTATION, {
    refetchQueries,
    onError,
  });
  const [decline, { loading: declineLoading }] = useMutation(NEGOTIATED_QUOTE_DECLINE_MUTATION, {
    refetchQueries,
    onError,
  });

  const negotiationCaseStatus = data?.tesseract.negotiationCase.status;
  const negotiatedQuote = data?.tesseract.negotiationCase.negotiatedQuote;
  const isMaskedBidding = data?.tesseract.negotiationCase.isMaskedBidding;

  let content: React.ReactNode;

  const handleDecline: DeclineButtonProps['onSubmit'] = async (data) => {
    await decline({
      variables: {
        negotiationCaseId: negotiationId,
        data: {
          text: data.message,
        },
      },
    });
  };
  const handleAccept: AcceptButtonProps['onSubmit'] = async (data) => {
    await accept({
      variables: {
        negotiationCaseId: negotiationId,
        data: {
          text: data.message,
        },
      },
    });
  };

  if (!error) {
    if (loading || negotiatedQuote) {
      content = (
        <CurrencyProvider value={negotiatedQuote?.currency || DEFAULT_CURRENCY}>
          <NegotiatedQuoteDetails isMasked={isMaskedBidding} data={negotiatedQuote} loading={loading} />
        </CurrencyProvider>
      );
    } else {
      content = <WidgetContentEmpty title={t('empty')} />;
    }
  } else {
    content = <WidgetContentEmpty title={t('error')} color="error.main" />;
  }

  return (
    <Widget hover={false} data-intercom-target="NegotiatedQuoteWidget">
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<RequestQuoteOutlined fontSize="small" />} />}
        actionsRight={
          negotiationCaseStatus === TsrctNegotiationCaseStatus.MANUAL_NEGOTIATION ? (
            <>
              <DeclineButton
                loading={declineLoading}
                disabled={isMaskedBidding || acceptLoading}
                onSubmit={handleDecline}
              />
              <AcceptButton
                loading={acceptLoading}
                disabled={isMaskedBidding || declineLoading}
                onSubmit={handleAccept}
              />
            </>
          ) : null
        }
      />
      <WidgetContent>{content}</WidgetContent>
    </Widget>
  );
};
